import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { fetchAuthenticatedJson } from '../../api/authenticatedFetch';

const jobs = [
  {
    name: 'UniverseBuilderJob',
    label: 'Universe Builder',
    api: '/api/admin/job/universe-builder',
    description: 'Reconstruit l univers de travail et les actifs suivis par le cockpit.',
    icon: <PrecisionManufacturingRoundedIcon />,
  },
  {
    name: 'AssetScannerJob',
    label: 'Asset Scanner',
    api: '/api/admin/job/asset-scanner',
    description: 'Relance le scan principal pour recalculer les opportunites et signaux.',
    icon: <AutoGraphRoundedIcon />,
  },
  {
    name: 'PatternScannerJob',
    label: 'Pattern Scanner',
    api: '/api/admin/job/pattern-scanner',
    description: 'Declenche l analyse des patterns techniques sur l univers courant.',
    icon: <TimelineRoundedIcon />,
  },
  {
    name: 'CryptoPriceHistoryJob',
    label: 'Historique des prix crypto',
    api: '/api/admin/job/crypto-price-history',
    description: 'Met a jour l historique de prix utile aux calculs et vues retroactives.',
    icon: <SavingsRoundedIcon />,
  },
  {
    name: 'SimulatePriceHistory',
    label: 'Simuler 60 jours de prix',
    api: '/api/admin/job/simulate-price-history',
    description: 'Genere un jeu de donnees de prix pour enrichir un environnement de dev.',
    icon: <PlayArrowRoundedIcon />,
  },
];

const DEFAULT_PATTERN_SCANNER_UNIVERSE_SIZE = 100;
const PATTERN_SCANNER_COHORT_PRESETS = [10, 20, 50, DEFAULT_PATTERN_SCANNER_UNIVERSE_SIZE];
const DEFAULT_PATTERN_SCANNER_ENGINE_VERSIONS = '1,3,4,6,7';
const TELEGRAM_AUTO_ALERT_STATUS_API = '/api/admin/job/pattern-telegram-auto-alert-status';

function StatBadge({ label, value, accent = '#19323c' }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(247,243,234,0.12)',
        minWidth: 132,
      }}
    >
      <Typography variant="caption" sx={{ color: 'rgba(247,243,234,0.72)', display: 'block', mb: 0.35 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: accent, fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );
}

function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('fr-FR');
}

function formatDuration(value) {
  if (value == null) {
    return 'n/a';
  }

  return `${Math.round(value * 10) / 10} ms`;
}

function formatPercent(value) {
  if (value == null) {
    return 'n/a';
  }

  return `${Math.round(value * 10) / 10}%`;
}

function formatBytes(value) {
  if (value == null) {
    return 'n/a';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let current = Math.abs(value);
  let index = 0;

  while (current >= 1024 && index < units.length - 1) {
    current /= 1024;
    index += 1;
  }

  const signed = value < 0 ? -current : current;
  return `${Math.round(signed * 10) / 10} ${units[index]}`;
}

function compactValue(value) {
  return value == null || value === '' ? 'n/a' : `${value}`;
}

function resolveBenchmarkAccent(status) {
  if (status === 'VALID') {
    return '#8fd6b1';
  }
  if (status === 'INVALID' || status === 'FAILED') {
    return '#d96c6c';
  }
  if (status === 'PARTIAL') {
    return '#f3c65c';
  }
  return '#19323c';
}

function resolveOrchestrationLabel(diagnostic) {
  if (!diagnostic) {
    return 'LEGACY';
  }
  if (diagnostic.executionModel === 'SHARED_COHORT_PARALLEL_PER_ENGINE') {
    return 'V4.4';
  }
  return 'LEGACY';
}

function resolveTelegramAutoAlertAccent(status) {
  if (status === 'READY') {
    return '#8fd6b1';
  }
  if (status === 'BLOCKED') {
    return '#d96c6c';
  }
  return '#19323c';
}

function resolveTelegramDestinationLabel(status) {
  if (!status) {
    return 'n/a';
  }
  if (status.destinationResolved) {
    return 'UNIQUE';
  }
  if (status.destinationResolutionReason === 'AMBIGUOUS_ADMIN_TELEGRAM_DESTINATION') {
    return 'AMBIGUOUS';
  }
  if (status.destinationResolutionReason === 'NO_ADMIN_TELEGRAM_DESTINATION') {
    return 'MISSING';
  }
  return 'BLOCKED';
}

function formatList(values, fallback = 'Aucun') {
  return Array.isArray(values) && values.length > 0 ? values.join(', ') : fallback;
}

function formatTelegramAutoAlertPolicyText(status) {
  const pattern = compactValue(status?.eligiblePattern);
  const direction = compactValue(status?.eligibleDirection);
  const timeframe = compactValue(status?.eligibleTimeframe);

  if (pattern === 'CHANNEL_BREAKOUT' && direction === 'BULLISH' && timeframe === '4H') {
    return 'aux CHANNEL_BREAKOUT haussiers en H4';
  }
  if (pattern !== 'n/a' && direction !== 'n/a' && timeframe !== 'n/a') {
    return `aux ${pattern} ${direction} en ${timeframe}`;
  }
  if (pattern !== 'n/a' && direction !== 'n/a') {
    return `aux ${pattern} ${direction}`;
  }
  if (pattern !== 'n/a') {
    return `aux ${pattern}`;
  }
  return 'aux patterns eligibles';
}

function resolvePreferredBenchmarkEngine(benchmark) {
  if (!benchmark?.engines?.length) {
    return '';
  }

  const rankedEngines = [...benchmark.engines].sort((left, right) => {
    const leftProblems = (left.notExecutedExecutions || 0) + (left.failedExecutions || 0) + (left.timeoutExecutions || 0) + (left.pendingExecutions || 0) + (left.runningExecutions || 0);
    const rightProblems = (right.notExecutedExecutions || 0) + (right.failedExecutions || 0) + (right.timeoutExecutions || 0) + (right.pendingExecutions || 0) + (right.runningExecutions || 0);

    if (rightProblems !== leftProblems) {
      return rightProblems - leftProblems;
    }

    return `${left.engineVersion}`.localeCompare(`${right.engineVersion}`);
  });

  return rankedEngines[0]?.engineVersion || '';
}

function buildBenchmarkCohortDebugUrl(runId, engineVersion, includeAll) {
  const params = new URLSearchParams();
  if (runId) {
    params.set('runId', runId);
  }
  if (engineVersion) {
    params.set('engineVersion', engineVersion);
  }
  params.set('includeAll', includeAll ? 'true' : 'false');
  return `/api/admin/job/pattern-scanner-benchmark-cohort-debug?${params.toString()}`;
}

const JobAdminPage = () => {
  const [loadingJob, setLoadingJob] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [lastRun, setLastRun] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(true);
  const [diagnosticError, setDiagnosticError] = useState('');
  const [diagnostic, setDiagnostic] = useState(null);
  const [cohortDebugLoading, setCohortDebugLoading] = useState(false);
  const [cohortDebugError, setCohortDebugError] = useState('');
  const [cohortDebug, setCohortDebug] = useState(null);
  const [selectedBenchmarkEngine, setSelectedBenchmarkEngine] = useState('');
  const [includeAllBenchmarkExecutions, setIncludeAllBenchmarkExecutions] = useState(false);
  const [patternScannerUniverseSize, setPatternScannerUniverseSize] = useState(DEFAULT_PATTERN_SCANNER_UNIVERSE_SIZE);
  const [telegramAutoAlertLoading, setTelegramAutoAlertLoading] = useState(true);
  const [telegramAutoAlertError, setTelegramAutoAlertError] = useState('');
  const [telegramAutoAlertStatus, setTelegramAutoAlertStatus] = useState(null);

  const idle = useMemo(() => !loadingJob, [loadingJob]);
  const recentEvents = useMemo(() => (diagnostic?.events || []).slice(-8).reverse(), [diagnostic]);
  const benchmark = diagnostic?.benchmark || null;
  const diagnosticEnginesByVersion = useMemo(
    () => new Map((diagnostic?.engines || []).map((engine) => [engine.engineVersion, engine])),
    [diagnostic],
  );
  const orchestrationLabel = useMemo(() => resolveOrchestrationLabel(diagnostic), [diagnostic]);

  const loadTelegramAutoAlertStatus = useCallback(async (showSpinner = true) => {
    if (showSpinner) {
      setTelegramAutoAlertLoading(true);
    }
    setTelegramAutoAlertError('');

    try {
      const payload = await fetchAuthenticatedJson(TELEGRAM_AUTO_ALERT_STATUS_API);
      setTelegramAutoAlertStatus(payload);
    } catch (e) {
      setTelegramAutoAlertStatus(null);
      setTelegramAutoAlertError(e.message);
    } finally {
      setTelegramAutoAlertLoading(false);
    }
  }, []);

  const loadBenchmarkDebug = useCallback(async (runId, engineVersion, includeAll = false) => {
    if (!runId) {
      setSelectedBenchmarkEngine('');
      setIncludeAllBenchmarkExecutions(includeAll);
      setCohortDebug(null);
      setCohortDebugError('');
      setCohortDebugLoading(false);
      return;
    }

    setCohortDebugLoading(true);
    setCohortDebugError('');
    setSelectedBenchmarkEngine(engineVersion || '');
    setIncludeAllBenchmarkExecutions(includeAll);

    try {
      const payload = await fetchAuthenticatedJson(buildBenchmarkCohortDebugUrl(runId, engineVersion, includeAll));
      setCohortDebug(payload);
    } catch (e) {
      setCohortDebug(null);
      setCohortDebugError(e.message);
    } finally {
      setCohortDebugLoading(false);
    }
  }, []);

  const loadDiagnostic = useCallback(async (showSpinner = true) => {
    if (showSpinner) {
      setDiagnosticLoading(true);
    }
    setDiagnosticError('');

    try {
      const payload = await fetchAuthenticatedJson('/api/admin/job/pattern-scanner-diagnostic');
      setDiagnostic(payload);
      if (payload?.benchmark?.runId) {
        await loadBenchmarkDebug(
          payload.benchmark.runId,
          resolvePreferredBenchmarkEngine(payload.benchmark),
          false,
        );
      } else {
        setSelectedBenchmarkEngine('');
        setIncludeAllBenchmarkExecutions(false);
        setCohortDebug(null);
        setCohortDebugError('');
      }
    } catch (e) {
      setDiagnosticError(e.message);
    } finally {
      setDiagnosticLoading(false);
    }
  }, [loadBenchmarkDebug]);

  useEffect(() => {
    const bootstrapAdminPanels = async () => {
      await loadDiagnostic();
      await loadTelegramAutoAlertStatus();
    };

    bootstrapAdminPanels();
  }, [loadDiagnostic, loadTelegramAutoAlertStatus]);

  const buildJobRequestUrl = (job) => {
    if (!job || job.name !== 'PatternScannerJob') {
      return job?.api || '';
    }
    const params = new URLSearchParams();
    params.set('patternEngineVersions', DEFAULT_PATTERN_SCANNER_ENGINE_VERSIONS);
    if (patternScannerUniverseSize !== DEFAULT_PATTERN_SCANNER_UNIVERSE_SIZE) {
      params.set('universeSize', String(patternScannerUniverseSize));
    }
    const queryString = params.toString();
    return queryString ? `${job.api}?${queryString}` : job.api;
  };

  const handleRunJob = async (api) => {
    const currentJob = jobs.find((job) => job.api === api);
    const requestUrl = buildJobRequestUrl(currentJob);
    setLoadingJob(currentJob?.name || api);
    setError('');
    setSuccessMessage('');
    try {
      const payload = await fetchAuthenticatedJson(requestUrl || api, { method: 'POST' });
      if (payload?.status === 'FAILED') {
        throw new Error(payload?.message || 'Erreur lors du déclenchement du job');
      }
      const message = payload?.message || 'Job declenche avec succes';
      setSuccessMessage(message);
      setLastRun({
        label: currentJob?.label || 'Job admin',
        message,
        at: new Date().toLocaleString('fr-FR'),
      });

      if (currentJob?.name === 'PatternScannerJob') {
        await loadDiagnostic(false);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingJob('');
    }
  };

  const handleBenchmarkEngineChange = async (engineVersion) => {
    if (!benchmark?.runId) {
      return;
    }
    await loadBenchmarkDebug(benchmark.runId, engineVersion, includeAllBenchmarkExecutions);
  };

  const handleToggleBenchmarkExecutions = async () => {
    if (!benchmark?.runId) {
      return;
    }
    const nextIncludeAll = !includeAllBenchmarkExecutions;
    await loadBenchmarkDebug(
      benchmark.runId,
      selectedBenchmarkEngine || resolvePreferredBenchmarkEngine(benchmark),
      nextIncludeAll,
    );
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Admin Jobs" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Declencher les traitements manuels de l'administration
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Cette page permet de lancer les jobs backend a la demande pour reconstruire l'univers, rescanner les actifs, recalculer les patterns ou remplir l'historique de prix.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              <StatBadge label="Jobs disponibles" value={`${jobs.length}`} accent="#f7f3ea" />
              <StatBadge label="Etat" value={idle ? 'IDLE' : 'RUNNING'} accent={idle ? '#8fd6b1' : '#f3c65c'} />
              <StatBadge label="Dernier lancement" value={lastRun?.label || 'Aucun'} accent={lastRun ? '#f3c65c' : '#f7f3ea'} />
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
          <Paper sx={{ flex: 1.15, p: 3, borderRadius: 5, background: '#fffaf2' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Actions disponibles
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Chaque bouton declenche directement l'endpoint admin correspondant. L'execution backend continue ensuite selon la logique du job appele.
            </Typography>

            <Stack spacing={1.8}>
              {jobs.map((job) => {
                const isRunning = loadingJob === job.name;
                return (
                  <Paper
                    key={job.name}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      background: '#fff',
                      border: '1px solid rgba(18,44,52,0.08)',
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f6f1e5',
                            color: '#17323c',
                            flexShrink: 0,
                          }}
                        >
                          {job.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#19323c', mb: 0.35 }}>
                            {job.label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#4f646f' }}>
                            {job.description}
                          </Typography>
                          {job.name === 'PatternScannerJob' ? (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mt: 1.25 }}>
                              <FormControl size="small" sx={{ minWidth: 170 }}>
                                <InputLabel id="pattern-scanner-cohort-size-label">Benchmark cohort</InputLabel>
                                <Select
                                  labelId="pattern-scanner-cohort-size-label"
                                  label="Benchmark cohort"
                                  value={patternScannerUniverseSize}
                                  onChange={(event) => setPatternScannerUniverseSize(Number(event.target.value) || DEFAULT_PATTERN_SCANNER_UNIVERSE_SIZE)}
                                >
                                  {PATTERN_SCANNER_COHORT_PRESETS.map((size) => (
                                    <MenuItem key={size} value={size}>
                                      {size === DEFAULT_PATTERN_SCANNER_UNIVERSE_SIZE ? `${size} actifs (defaut)` : `${size} actifs`}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Typography variant="caption" sx={{ color: '#6c7a80' }}>
                                {`Mode petite cohorte pour valider rapidement cohorte, duplication et snapshots sur 10 / 20 / 50 actifs avant un benchmark complet. V4.2 est retiree des executions actives; le Pattern Scanner lance par defaut tous les moteurs actifs ${DEFAULT_PATTERN_SCANNER_ENGINE_VERSIONS}.`}
                              </Typography>
                            </Stack>
                          ) : null}
                        </Box>
                      </Stack>

                      <Button
                        variant={isRunning ? 'outlined' : 'contained'}
                        onClick={() => handleRunJob(job.api)}
                        disabled={!idle}
                        startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : <PlayArrowRoundedIcon />}
                        sx={{
                          minHeight: 48,
                          minWidth: 180,
                          borderRadius: 99,
                          bgcolor: isRunning ? 'transparent' : '#17323c',
                          color: isRunning ? '#17323c' : '#f7f3ea',
                          borderColor: '#17323c',
                          ':hover': isRunning ? { borderColor: '#17323c' } : { bgcolor: '#0f2329' },
                        }}
                      >
                        {isRunning ? 'Execution...' : `Lancer ${job.label}`}
                      </Button>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>

          <Paper sx={{ flex: 0.92, p: 3, borderRadius: 5, background: '#fff' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Etat et retour
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Les retours de l'API admin sont affiches ici pour eviter les popups navigateur et garder une lecture coherente avec le reste du back-office.
            </Typography>

            <Stack spacing={2}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                  Regles d'execution
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Un seul declenchement est permis a la fois depuis cette page. Lorsque le backend confirme l'appel, le message de succes ou d'erreur reste visible ici jusqu'au prochain lancement.
                </Typography>
              </Paper>

              {error && <Alert severity="error">{error}</Alert>}
              {successMessage && <Alert severity="success">{successMessage}</Alert>}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} useFlexGap flexWrap="wrap">
                <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 132 }}>
                  <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                    Job courant
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                    {loadingJob ? jobs.find((job) => job.name === loadingJob)?.label || loadingJob : 'Aucun'}
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 132 }}>
                  <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                    Derniere action
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                    {lastRun?.label || 'Aucune'}
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 132 }}>
                  <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                    Horodatage
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                    {lastRun?.at || 'n/a'}
                  </Typography>
                </Paper>
              </Stack>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#f8fbff', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                  Dernier message
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                  {lastRun?.message || 'Aucun job n a encore ete declenche depuis cette page.'}
                </Typography>
              </Paper>
            </Stack>
          </Paper>
        </Stack>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }} sx={{ mb: 2.5 }}>
            <Box sx={{ maxWidth: 820 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
                Alerte Telegram automatique
              </Typography>
              <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                Cette carte expose l'etat operatoire du job Telegram auto DB-only et la politique de publication actuellement autorisee par l'engine officiel, avec blocage explicite si la destination Telegram admin n'est pas resolue de facon unique.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap">
              <StatBadge label="Statut" value={telegramAutoAlertStatus?.status || 'n/a'} accent={resolveTelegramAutoAlertAccent(telegramAutoAlertStatus?.status)} />
              <StatBadge label="Serveur Telegram" value={telegramAutoAlertStatus ? (telegramAutoAlertStatus.sendAvailable ? 'READY' : 'BLOCKED') : 'n/a'} accent={telegramAutoAlertStatus?.sendAvailable ? '#8fd6b1' : '#d96c6c'} />
              <StatBadge label="Destination" value={resolveTelegramDestinationLabel(telegramAutoAlertStatus)} accent={telegramAutoAlertStatus?.destinationResolved ? '#8fd6b1' : '#d96c6c'} />
              <Button
                variant="outlined"
                onClick={() => loadTelegramAutoAlertStatus()}
                disabled={telegramAutoAlertLoading || !idle}
                startIcon={telegramAutoAlertLoading ? <CircularProgress size={18} color="inherit" /> : <TimelineRoundedIcon />}
                sx={{ minHeight: 48, borderRadius: 99, borderColor: '#17323c', color: '#17323c' }}
              >
                {telegramAutoAlertLoading ? 'Lecture...' : 'Rafraichir alerte Telegram'}
              </Button>
            </Stack>
          </Stack>

          {telegramAutoAlertError && <Alert severity="error" sx={{ mb: 2 }}>{telegramAutoAlertError}</Alert>}

          {!telegramAutoAlertLoading && !telegramAutoAlertError && telegramAutoAlertStatus?.status === 'BLOCKED' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {telegramAutoAlertStatus.sendStatusMessage || 'Le flux Telegram auto est bloque.'}
            </Alert>
          )}

          {!telegramAutoAlertLoading && !telegramAutoAlertError && telegramAutoAlertStatus?.status === 'READY' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {`Le flux Telegram auto est pret. Les alertes restent limitees ${formatTelegramAutoAlertPolicyText(telegramAutoAlertStatus)} issus du job DB-only et de l'engine officiel.`}
            </Alert>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} useFlexGap flexWrap="wrap" sx={{ mb: 2.5 }}>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Trigger source
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {telegramAutoAlertStatus?.triggerSource || 'n/a'}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Pattern cible
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {telegramAutoAlertStatus?.eligiblePattern || 'n/a'}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Direction cible
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {telegramAutoAlertStatus?.eligibleDirection || 'n/a'}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Timeframe cible
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {telegramAutoAlertStatus?.eligibleTimeframe || 'n/a'}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Destination resolue
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {telegramAutoAlertStatus?.resolvedChatIdPreview || 'n/a'}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Admins configures
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {telegramAutoAlertStatus?.adminsWithTelegramChatIdCount ?? 'n/a'}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Destinations distinctes
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {telegramAutoAlertStatus?.distinctDestinationCount ?? 'n/a'}
              </Typography>
            </Paper>
          </Stack>

          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} alignItems="stretch">
            <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 4, background: '#fffaf2', border: '1px solid rgba(18,44,52,0.08)' }}>
              <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                Admins avec chatId
              </Typography>
              <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                {formatList(telegramAutoAlertStatus?.adminsWithTelegramChatId)}
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ flex: 1, p: 2, borderRadius: 4, background: '#fffaf2', border: '1px solid rgba(18,44,52,0.08)' }}>
              <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                Destinations detectees
              </Typography>
              <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                {formatList(telegramAutoAlertStatus?.distinctDestinationPreviews)}
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ flex: 1.2, p: 2, borderRadius: 4, background: '#f8fbff', border: '1px solid rgba(18,44,52,0.08)' }}>
              <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                Raisons de blocage
              </Typography>
              {(telegramAutoAlertStatus?.blockingReasons || []).length ? (
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                  {telegramAutoAlertStatus.blockingReasons.map((reason) => (
                    <Chip key={reason} size="small" label={reason} sx={{ bgcolor: '#fcebea', color: '#9b2c2c', fontWeight: 700 }} />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                  Aucune. La destination admin unique et la configuration serveur Telegram sont resolues.
                </Typography>
              )}
            </Paper>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }} sx={{ mb: 2.5 }}>
            <Box sx={{ maxWidth: 820 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
                Diagnostic multi-engine
              </Typography>
              <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                Cette vue lit directement le dernier snapshot backend du Pattern Scanner pour distinguer dispatch, execution, qualification, persistence et filtrage par engine.
              </Typography>
              {orchestrationLabel === 'V4.4' && (
                <Chip
                  label="V4.4 benchmark orchestration"
                  sx={{ mt: 1.25, bgcolor: '#e9f7ef', color: '#19323c', fontWeight: 800 }}
                />
              )}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap">
              <StatBadge label="Statut" value={diagnostic?.status || 'IDLE'} accent={diagnostic?.status === 'FAILED' ? '#d96c6c' : diagnostic?.status === 'SUCCESS' ? '#8fd6b1' : '#19323c'} />
              <StatBadge label="Orchestration" value={orchestrationLabel} accent={orchestrationLabel === 'V4.4' ? '#8fd6b1' : '#19323c'} />
              <StatBadge label="Execution" value={diagnostic?.executionModel || 'n/a'} accent="#f7f3ea" />
              <StatBadge label="Benchmark" value={benchmark?.status || 'n/a'} accent={resolveBenchmarkAccent(benchmark?.status)} />
              <StatBadge label="Active engines" value={diagnostic?.activeEngines?.join(', ') || 'n/a'} accent="#f3c65c" />
              <Button
                variant="outlined"
                onClick={() => loadDiagnostic()}
                disabled={diagnosticLoading || !idle}
                startIcon={diagnosticLoading ? <CircularProgress size={18} color="inherit" /> : <TimelineRoundedIcon />}
                sx={{ minHeight: 48, borderRadius: 99, borderColor: '#17323c', color: '#17323c' }}
              >
                {diagnosticLoading ? 'Lecture...' : 'Rafraichir diagnostic'}
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} useFlexGap flexWrap="wrap" sx={{ mb: 2.5 }}>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Scan ID
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {diagnostic?.scanId || 'Aucun'}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Trigger
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {diagnostic?.trigger || 'n/a'}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Debut
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {formatDateTime(diagnostic?.startedAt)}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Fin
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {formatDateTime(diagnostic?.finishedAt)}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Analyses rafraichies
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {compactValue(diagnostic?.refreshedAnalyses)}
              </Typography>
            </Paper>
            <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 168 }}>
              <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                Detection persistence
              </Typography>
              <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                {diagnostic?.detectionPersistenceModel || 'n/a'}
              </Typography>
            </Paper>
          </Stack>

          {diagnosticError && <Alert severity="error" sx={{ mb: 2 }}>{diagnosticError}</Alert>}

          {!diagnosticLoading && !diagnostic?.scanId && !diagnosticError && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Aucun cycle de Pattern Scanner n'a encore ete observe par le diagnostic.
            </Alert>
          )}

          {benchmark && (
            <Stack spacing={1.5} sx={{ mb: 2.5 }}>
              {!benchmark.comparisonAvailable && (
                <Alert severity={benchmark.status === 'FAILED' || benchmark.status === 'INVALID' ? 'warning' : 'info'}>
                  La comparaison inter-engines reste invalide tant que tous les engines n'ont pas termine la meme cohorte d'entree.
                </Alert>
              )}

              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.8} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }}>
                  <Box sx={{ maxWidth: 780 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
                      Benchmark execution progress
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                      V4.4 ne designe pas un nouveau moteur de detection. C'est la couche d'orchestration benchmark: cohorte partagee, execution isolee par engine, progression durable et invalidation des comparaisons partielles.
                    </Typography>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap">
                    <StatBadge label="Benchmark run" value={compactValue(benchmark.runId)} accent="#17323c" />
                    <StatBadge label="Cohort" value={compactValue(benchmark.cohortId)} accent="#17323c" />
                    <StatBadge label="Statut" value={benchmark.status || 'n/a'} accent={resolveBenchmarkAccent(benchmark.status)} />
                    <StatBadge label="Progression" value={`${benchmark.completedExecutions} / ${benchmark.expectedExecutions}`} accent="#17323c" />
                    <StatBadge label="Coverage" value={formatPercent(benchmark.coveragePercent)} accent="#17323c" />
                  </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                  <StatBadge label="Dispatched" value={compactValue(benchmark.dispatchedExecutions)} accent="#17323c" />
                  <StatBadge label="Started" value={compactValue(benchmark.startedExecutions)} accent="#17323c" />
                  <StatBadge label="Pending" value={compactValue(benchmark.pendingExecutions)} accent={benchmark.pendingExecutions > 0 ? '#d96c6c' : '#19323c'} />
                  <StatBadge label="Not executed" value={compactValue(benchmark.notExecutedExecutions)} accent={benchmark.notExecutedExecutions > 0 ? '#d96c6c' : '#19323c'} />
                  <StatBadge label="Running" value={compactValue(benchmark.runningExecutions)} accent="#19323c" />
                  <StatBadge label="Failed" value={compactValue(benchmark.failedExecutions)} accent={benchmark.failedExecutions > 0 ? '#d96c6c' : '#19323c'} />
                  <StatBadge label="Timeout" value={compactValue(benchmark.timeoutExecutions)} accent={benchmark.timeoutExecutions > 0 ? '#d96c6c' : '#19323c'} />
                  <StatBadge label="Comparison" value={benchmark.comparisonAvailable ? 'AVAILABLE' : 'UNAVAILABLE'} accent={benchmark.comparisonAvailable ? '#8fd6b1' : '#d96c6c'} />
                  <StatBadge label="Debut benchmark" value={formatDateTime(benchmark.startedAt)} accent="#17323c" />
                </Stack>
              </Paper>

              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.6} useFlexGap flexWrap="wrap">
                {(benchmark.engines || []).map((engine) => {
                  const observedEngine = diagnosticEnginesByVersion.get(engine.engineVersion);
                  const issueCount = (engine.notExecutedExecutions || 0) + (engine.failedExecutions || 0) + (engine.timeoutExecutions || 0) + (engine.pendingExecutions || 0) + (engine.runningExecutions || 0);
                  return (
                  <Paper
                    key={`benchmark-${engine.engineVersion}`}
                    elevation={0}
                    sx={{
                      flex: '1 1 280px',
                      p: 2,
                      borderRadius: 4,
                      background: '#fff',
                      border: '1px solid rgba(18,44,52,0.08)',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1.2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#19323c' }}>
                        {engine.engineVersion}
                      </Typography>
                      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                        <Chip
                          size="small"
                          label={`${engine.startedExecutions ?? engine.executedExecutions ?? engine.completedExecutions} / ${engine.expectedExecutions}`}
                          sx={{ bgcolor: '#eef3f6', color: '#19323c', fontWeight: 700 }}
                        />
                        {issueCount > 0 && (
                          <Chip
                            size="small"
                            label={`Issues ${issueCount}`}
                            onClick={() => handleBenchmarkEngineChange(engine.engineVersion)}
                            sx={{ bgcolor: '#fcebea', color: '#9b2c2c', fontWeight: 700 }}
                          />
                        )}
                      </Stack>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mb: 1.3 }}>
                      <StatBadge label="Expected" value={compactValue(engine.expectedExecutions)} accent="#17323c" />
                      <StatBadge label="Dispatched" value={compactValue(engine.dispatchedExecutions ?? observedEngine?.dispatched)} accent="#17323c" />
                      <StatBadge label="Started" value={compactValue(engine.startedExecutions)} accent="#17323c" />
                      <StatBadge label="Not executed" value={compactValue(engine.notExecutedExecutions)} accent={engine.notExecutedExecutions > 0 ? '#d96c6c' : '#17323c'} />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mb: 1.3 }}>
                      <StatBadge label="Completed" value={compactValue(engine.completedExecutions)} accent="#17323c" />
                      <StatBadge label="Failed" value={compactValue(engine.failedExecutions)} accent={engine.failedExecutions > 0 ? '#d96c6c' : '#17323c'} />
                      <StatBadge label="Timeout" value={compactValue(engine.timeoutExecutions)} accent={engine.timeoutExecutions > 0 ? '#d96c6c' : '#17323c'} />
                      <StatBadge label="Errors" value={compactValue(engine.executionErrorCount)} accent={engine.executionErrorCount > 0 ? '#d96c6c' : '#17323c'} />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mb: 1.3 }}>
                      <StatBadge label="Coverage" value={formatPercent(engine.coveragePercent)} accent="#17323c" />
                      <StatBadge label="Pending/Running" value={`${compactValue(engine.pendingExecutions)} / ${compactValue(engine.runningExecutions)}`} accent="#17323c" />
                      <StatBadge label="Detected/Zero" value={`${compactValue(observedEngine?.detected)} / ${compactValue(observedEngine?.detectedZero)}`} accent="#17323c" />
                      <StatBadge label="Filtered" value={compactValue(observedEngine?.filtered)} accent="#17323c" />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mb: 1.3 }}>
                      <StatBadge label="Analyses persisted" value={compactValue(observedEngine?.analysesPersisted)} accent="#17323c" />
                      <StatBadge label="Detections persisted" value={compactValue(observedEngine?.detectionsPersisted)} accent="#17323c" />
                      <StatBadge label="Avg queue" value={formatDuration(engine.averageQueueWaitMs)} accent="#17323c" />
                      <StatBadge label="Avg duration" value={formatDuration(engine.averageExecutionDurationMs)} accent="#17323c" />
                    </Stack>

                    <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                      First start {formatDateTime(engine.firstExecutionStartedAt)} | Last finish {formatDateTime(engine.lastExecutionFinishedAt)} | Max duration {formatDuration(engine.maxExecutionDurationMs)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                      Remaining {compactValue(engine.remainingExecutions)} | Snapshot {formatDateTime(engine.latestInputSnapshotCandleTimestamp)}
                    </Typography>
                  </Paper>
                );})}
              </Stack>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#f8fbff', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.6} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }} sx={{ mb: 1.5 }}>
                  <Box sx={{ maxWidth: 760 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
                      Cohort debug
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                      Cette vue liste les execution keys benchmark pour l engine selectionne afin de distinguer `NOT_EXECUTED`, echec d execution et progression partielle sans perte silencieuse.
                    </Typography>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap">
                    <StatBadge label="Engine" value={selectedBenchmarkEngine || 'n/a'} accent="#17323c" />
                    <StatBadge label="Problem rows" value={compactValue(cohortDebug?.problemExecutions)} accent={cohortDebug?.problemExecutions > 0 ? '#d96c6c' : '#17323c'} />
                    <StatBadge label="Expected" value={compactValue(cohortDebug?.expectedExecutions)} accent="#17323c" />
                    <StatBadge label="Completed" value={compactValue(cohortDebug?.completedExecutions)} accent="#17323c" />
                    <Button
                      variant="outlined"
                      onClick={handleToggleBenchmarkExecutions}
                      disabled={cohortDebugLoading || !benchmark?.runId}
                      sx={{ minHeight: 44, borderRadius: 99, borderColor: '#17323c', color: '#17323c' }}
                    >
                      {includeAllBenchmarkExecutions ? 'Masquer les executions saines' : 'Voir toutes les executions'}
                    </Button>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                  {(benchmark.engines || []).map((engine) => {
                    const issueCount = (engine.notExecutedExecutions || 0) + (engine.failedExecutions || 0) + (engine.timeoutExecutions || 0) + (engine.pendingExecutions || 0) + (engine.runningExecutions || 0);
                    const selected = selectedBenchmarkEngine === engine.engineVersion;
                    return (
                      <Chip
                        key={`debug-engine-${engine.engineVersion}`}
                        label={`${engine.engineVersion} | issues ${issueCount}`}
                        onClick={() => handleBenchmarkEngineChange(engine.engineVersion)}
                        sx={{
                          bgcolor: selected ? '#17323c' : '#eef3f6',
                          color: selected ? '#f7f3ea' : '#19323c',
                          fontWeight: 700,
                        }}
                      />
                    );
                  })}
                </Stack>

                {cohortDebugError && <Alert severity="error" sx={{ mb: 1.5 }}>{cohortDebugError}</Alert>}

                {cohortDebugLoading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <Typography variant="body2" sx={{ color: '#4f646f' }}>
                      Chargement du debug de cohorte...
                    </Typography>
                  </Stack>
                ) : (cohortDebug?.executions || []).length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#4f646f' }}>
                    Aucune execution problematique pour cet engine sur la cohorte courante.
                  </Typography>
                ) : (
                  <Stack spacing={1.1}>
                    {(cohortDebug?.executions || []).map((execution) => (
                      <Paper key={execution.executionId || execution.executionKey} elevation={0} sx={{ p: 1.4, borderRadius: 3, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 0.65 }}>
                          <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap">
                            <Chip size="small" label={execution.status} sx={{ bgcolor: execution.status === 'COMPLETED' ? '#e9f7ef' : '#fcebea', color: '#19323c', fontWeight: 700 }} />
                            <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 700 }}>
                              {execution.symbol} {execution.exchangeCode} {execution.timeframe}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6c7a80' }}>
                              {execution.executionKey}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ color: '#6c7a80' }}>
                            Snapshot {formatDateTime(execution.inputSnapshotCandleTimestamp)}
                          </Typography>
                        </Stack>

                        <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                          Attempt {compactValue(execution.attemptCount)} | Queue {formatDuration(execution.queueWaitMs)} | Duration {formatDuration(execution.lastDurationMs)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                          Not executed reason {compactValue(execution.notExecutedReason)} | Failure {compactValue(execution.failureMessage)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                          Hash {compactValue(execution.inputSnapshotHash)}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>
          )}

          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.5} alignItems="stretch">
            <Box sx={{ flex: 1.3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#19323c', mb: 1.2 }}>
                Vue par engine
              </Typography>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.6} useFlexGap flexWrap="wrap">
                {(diagnostic?.engines || []).map((engine) => (
                  <Paper
                    key={engine.engineVersion}
                    elevation={0}
                    sx={{
                      flex: '1 1 320px',
                      p: 2,
                      borderRadius: 4,
                      background: '#fffaf2',
                      border: '1px solid rgba(18,44,52,0.08)',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.2} sx={{ mb: 1.3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#19323c' }}>
                        {engine.engineVersion}
                      </Typography>
                      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                        <Chip size="small" label={engine.configuredActive ? 'ACTIVE' : 'OBSERVED'} sx={{ bgcolor: engine.configuredActive ? '#e9f7ef' : '#eef3f6', color: '#19323c', fontWeight: 700 }} />
                        {engine.failed > 0 && <Chip size="small" label={`FAILED ${engine.failed}`} sx={{ bgcolor: '#fcebea', color: '#9b2c2c', fontWeight: 700 }} />}
                      </Stack>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mb: 1.4 }}>
                      <StatBadge label="Dispatch/Start" value={`${engine.dispatched} / ${engine.started}`} />
                      <StatBadge label="Complete/Fail" value={`${engine.completed} / ${engine.failed}`} />
                      <StatBadge label="Detected/Zero" value={`${engine.detected} / ${engine.detectedZero}`} />
                      <StatBadge label="Qualified/Filtered" value={`${engine.qualified} / ${engine.filtered}`} />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mb: 1.4 }}>
                      <StatBadge label="Analyses persisted" value={compactValue(engine.analysesPersisted)} accent="#17323c" />
                      <StatBadge label="Detections persisted" value={compactValue(engine.detectionsPersisted)} accent="#17323c" />
                      <StatBadge label="Avg total" value={formatDuration(engine.averageTotalDurationMs)} accent="#17323c" />
                      <StatBadge label="Avg persistence" value={formatDuration(engine.averagePersistenceDurationMs)} accent="#17323c" />
                    </Stack>

                    <Stack spacing={0.8}>
                      <Typography variant="body2" sx={{ color: '#4f646f' }}>
                        Detection {formatDuration(engine.averageDetectionDurationMs)} | Qualification {formatDuration(engine.averageQualificationDurationMs)} | Max total {formatDuration(engine.maxTotalDurationMs)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4f646f' }}>
                        CPU process {formatPercent(engine.averageProcessCpuPercent)} | CPU system {formatPercent(engine.averageSystemCpuPercent)} | Memory delta {formatBytes(engine.averageMemoryDeltaBytes)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4f646f' }}>
                        Peak memory {formatBytes(engine.peakUsedMemoryBytes)} | DB active {compactValue(engine.peakDbActiveConnections)} | DB total {compactValue(engine.peakDbTotalConnections)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4f646f' }}>
                        Missing levels {compactValue(engine.missingRequiredLevels)} | No closed candle {compactValue(engine.noClosedCandle)} | Stale {compactValue(engine.stale)} | Duplicates {compactValue(engine.duplicates)}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>

            <Paper sx={{ flex: 0.88, p: 2, borderRadius: 4, background: '#f8fbff', border: '1px solid rgba(18,44,52,0.08)' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#19323c', mb: 1.2 }}>
                Derniers evenements
              </Typography>
              <Stack spacing={1.1}>
                {recentEvents.length === 0 && (
                  <Typography variant="body2" sx={{ color: '#4f646f' }}>
                    Aucun evenement detaille a afficher pour le moment.
                  </Typography>
                )}
                {recentEvents.map((event, index) => (
                  <Paper key={`${event.scanId}-${event.timestamp}-${event.stage}-${index}`} elevation={0} sx={{ p: 1.4, borderRadius: 3, background: '#fff', border: '1px solid rgba(18,44,52,0.08)' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 0.6 }}>
                      <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap">
                        <Chip size="small" label={event.stage} sx={{ bgcolor: '#eef3f6', color: '#19323c', fontWeight: 700 }} />
                        <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 700 }}>
                          {event.engineVersion}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4f646f' }}>
                          {event.symbol} {event.exchange} {event.timeframe}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: '#6c7a80' }}>
                        {formatDateTime(event.timestamp)}
                      </Typography>
                    </Stack>
                    {(event.executionKey || event.benchmarkRunId || event.closedCandleTimestamp) && (
                      <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.45 }}>
                        Benchmark {compactValue(event.benchmarkRunId)} | Execution {compactValue(event.executionKey)} | Closed candle {formatDateTime(event.closedCandleTimestamp)}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: '#4f646f' }}>
                      {event.detail || 'Aucun detail complementaire'}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default JobAdminPage;
